/**
 * DevTool Snapshot Builder
 *
 * Constructs an XState v5-compatible persisted snapshot for devtool
 * jump-to-state. Passing this to `createActor(machine, { snapshot })`
 * (via useMachine) restores both the parent machine state and the child
 * arc actor state — requiring zero per-arc code.
 *
 * The format matches XState v5's PersistedMachineSnapshot, which is what
 * actor.getPersistedSnapshot() produces.
 */

export interface DevtoolArcJumpSpec {
  /** Parent machine state name (e.g. "triaging", "identifying") */
  parentValue: string;
  /** Full parent machine context */
  parentContext: Record<string, unknown>;
  /**
   * Child actor ID matching the `id` in the parent's invoke block
   * (e.g. "triageArc", "identityArc"). This comes directly from the
   * pasted JSON's `arc.arcId` field — no mapping needed.
   */
  arcId: string;
  /** Arc-internal state value to jump to (e.g. "workersCompQuestion" or { ocrLicense: "licenseFrontPhoto" }) */
  arcStateName: string | Record<string, unknown>;
  /** Arc context to restore at the jumped state */
  arcContext: Record<string, unknown>;
}

/**
 * Builds an XState v5 persisted snapshot with a child arc actor restored
 * to a specific internal state.
 *
 * The returned object can be passed directly to `createActor(machine, { snapshot })`
 * (or `useMachine(machine, { snapshot })`). XState will restore the parent
 * machine to `parentValue` and the child arc to `arcStateName` with `arcContext`.
 */
export function buildDevtoolJumpSnapshot(
  spec: DevtoolArcJumpSpec,
): Record<string, unknown> {
  return {
    status: "active",
    output: undefined,
    error: undefined,
    value: spec.parentValue,
    context: spec.parentContext,
    historyValue: {},
    tags: [],
    children: {
      [spec.arcId]: {
        snapshot: {
          status: "active",
          output: undefined,
          error: undefined,
          value: spec.arcStateName,
          context: spec.arcContext,
          historyValue: {},
          tags: [],
          children: {},
        },
        src: spec.arcId,
        syncSnapshot: false,
      },
    },
  };
}
